import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { State } from "../../../reducer";
import { LayoutAdminCafe } from "../../../layout/layout_cafe";
import { isAdminLike } from "../../../utils/access";
import { AuditLog, getAuditLogs } from "../../../api/admin.api";
import { formatAudit } from "../../../utils/auditFormat";

const PAGE_SIZE = 50;

const AuditRow: React.FC<{ row: AuditLog }> = ({ row }) => {
  const [showRaw, setShowRaw] = useState(false);
  const when = moment(row.createdAt).format("D MMM YY · HH:mm");
  const f = formatAudit(row);
  const hasRaw =
    !!row.action ||
    (row.metadata && Object.keys(row.metadata).length > 0);

  return (
    <div className="rounded-2xl border border-ink-100 bg-white px-4 py-3 flex items-start gap-3">
      <span className="text-2xl leading-none mt-0.5 shrink-0" aria-hidden>{f.icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-ink-1000 leading-snug break-words">
          {f.summary}
        </p>
        {f.detail && (
          <p className="text-xs text-ink-500 mt-1 break-words">{f.detail}</p>
        )}
        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-ink-400">
          <span>{when}</span>
          <span>·</span>
          <span className="text-[10px] uppercase font-bold tracking-wider rounded-full bg-ink-100 text-ink-600 px-2 py-0.5">
            {row.actor?.role ?? "?"}
          </span>
          {hasRaw && (
            <button
              type="button"
              onClick={() => setShowRaw((v) => !v)}
              className="ml-auto text-ink-500 hover:text-ink-1000 underline-offset-2 hover:underline"
            >
              {showRaw ? "Hide details" : "Details"}
            </button>
          )}
        </div>
        {showRaw && (
          <div className="mt-2 rounded-lg bg-ink-50 text-ink-700 p-2 text-[11px] font-mono space-y-1">
            <div><span className="text-ink-500">action:</span> {row.action}</div>
            {row.targetType && (
              <div>
                <span className="text-ink-500">target:</span> {row.targetType}#{row.targetId ?? "?"}
                {row.targetName ? ` (${row.targetName})` : ""}
              </div>
            )}
            {row.metadata && Object.keys(row.metadata).length > 0 && (
              <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words">
                {JSON.stringify(row.metadata, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Spinner = () => (
  <div className="py-[60px] flex justify-center">
    <svg className="w-6 h-6 animate-spin text-ink-700" fill="none" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
      <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  </div>
);

function AuditLogInner() {
  const role = useSelector((s: State) => s.user?.role);
  const navigate = useNavigate();

  const [rows, setRows] = useState<AuditLog[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => { setPage(0); }, [debouncedQ]);

  useEffect(() => {
    if (role && !isAdminLike(role)) {
      navigate("/unauthorized", { replace: true });
    }
  }, [role, navigate]);

  const load = async () => {
    setRefreshing(true);
    setLoadError(null);
    try {
      const r = await getAuditLogs(PAGE_SIZE, page * PAGE_SIZE, debouncedQ);
      setRows(r.rows);
      setTotal(r.total);
    } catch (err) {
      console.error("audit log fetch", err);
      setLoadError("Could not load the audit log.");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedQ]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeLabel = useMemo(() => {
    if (total === 0) return "no events";
    const from = page * PAGE_SIZE + 1;
    const to = Math.min((page + 1) * PAGE_SIZE, total);
    return `${from}–${to} of ${total}`;
  }, [total, page]);

  return (
    <div className="min-h-screen bg-white font-sans pb-[96px]">
      <header className="px-5 pt-[24px] pb-[16px]">
        <p className="eyebrow-v2 mb-2">Admin</p>
        <h1 className="font-display font-black text-[28px] leading-[1.15] tracking-tightest text-ink-1000">
          Audit log
        </h1>
        <p className="text-sm text-ink-500 mt-2">
          Who changed what, and when. {rangeLabel}.
        </p>
      </header>

      <div className="border-b border-ink-100" />

      <div className="px-5 pt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={load}
          disabled={refreshing}
          className="ml-auto btn-v2-secondary btn-v2-sm"
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="px-5 pt-3">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search action, user name, or target…"
            aria-label="Search audit log"
            className="w-full h-11 pl-10 pr-9 rounded-2xl bg-ink-50 border-2 border-transparent focus:border-ink-1000 focus:bg-white focus:outline-none text-sm text-ink-1000"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full text-ink-500 hover:bg-ink-100 flex items-center justify-center"
              aria-label="Clear"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <main className="px-5 pt-4 space-y-2">
        {loadError ? (
          <div role="alert" className="py-[80px] flex flex-col items-center text-center">
            <p className="font-display font-black text-lg text-ink-1000">Something went wrong</p>
            <p className="text-sm text-ink-500 mt-1">{loadError}</p>
            <button
              type="button"
              onClick={load}
              disabled={refreshing}
              className="mt-4 btn-v2-secondary btn-v2-sm"
            >
              {refreshing ? "Retrying…" : "Retry"}
            </button>
          </div>
        ) : rows == null ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <div className="py-[80px] flex flex-col items-center text-center">
            <div className="w-[64px] h-[64px] rounded-full bg-ink-100 flex items-center justify-center mb-3 text-3xl">📋</div>
            <p className="font-display font-black text-lg text-ink-1000">No matches</p>
            <p className="text-sm text-ink-500 mt-1">
              {debouncedQ ? `Nothing matched "${debouncedQ}".` : "No admin actions yet."}
            </p>
          </div>
        ) : (
          rows.map((r) => <AuditRow key={r.id} row={r} />)
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 pt-4 pb-2">
            <button
              type="button"
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0 || refreshing}
              className="btn-v2-secondary btn-v2-sm disabled:opacity-40"
            >
              ← Prev
            </button>
            <span className="text-xs text-ink-500">
              Page {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1 || refreshing}
              className="btn-v2-secondary btn-v2-sm disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default function AuditLogPage() {
  return (
    <LayoutAdminCafe>
      <AuditLogInner />
    </LayoutAdminCafe>
  );
}
